import { replace } from 'lodash/fp';
import { Middleware } from 'redux';
import {
    BadRequestError,
    BusinessError,
    EnvironmentService,
    LoggerService,
    NetworkError,
    NotAuthorizedError,
    QuotaExceededError,
    ResponseError,
    TimeoutError,
} from '@mews/framework';
import {
    ERROR_COMMUNICATION_WITH_SERVER_FAILED,
    ERROR_FATAL,
    ERROR_QUOTA_EXCEEDED,
    ERROR_REQUEST_TIMEOUT,
    ERROR_SESSION_EXPIRED,
} from '../constants';
import { logErrorKey, logErrorMessage } from '../services/error/actions';

interface ErrorHandlerMiddlewareOptions {
    loggerService: LoggerService;
    environmentService: EnvironmentService;
}

export const createErrorHandlerMiddleware =
    ({ loggerService, environmentService }: ErrorHandlerMiddlewareOptions): Middleware =>
    (store) =>
    (next) =>
    (action) => {
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        const error = action?.error || (action?.payload && action.payload?.error);
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        if (error) {
            if (error instanceof ResponseError || error.name === ResponseError.name) {
                store.dispatch(logErrorKey(ERROR_COMMUNICATION_WITH_SERVER_FAILED));
            } else if (error instanceof NotAuthorizedError || error.name === NotAuthorizedError.name) {
                store.dispatch(logErrorKey(ERROR_SESSION_EXPIRED));
            } else if (error instanceof TimeoutError || error.name === TimeoutError.name) {
                store.dispatch(logErrorKey(ERROR_REQUEST_TIMEOUT));
            } else if (
                error instanceof BusinessError ||
                error.name === BusinessError.name ||
                error instanceof BadRequestError ||
                error.name === BadRequestError.name
            ) {
                // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
                if (error.message) {
                    store.dispatch(logErrorMessage(error.message));
                } else {
                    store.dispatch(logErrorKey(ERROR_FATAL));
                }
            } else if (error instanceof QuotaExceededError || error.name === QuotaExceededError.name) {
                store.dispatch(logErrorKey(ERROR_QUOTA_EXCEEDED));
            } else if (error instanceof NetworkError || error.name === NetworkError.name) {
                store.dispatch(logErrorKey(ERROR_COMMUNICATION_WITH_SERVER_FAILED));
                // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
            } else if (error) {
                store.dispatch(logErrorKey(ERROR_FATAL));
            }

            // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
            const stackTrace = replace(/\n/g, ';')(error.stack || error.stacktrace || '');
            loggerService.log('error-handler-log', 'ErrorHandlerMiddleware encountered an error', {
                error,
                action,
                stackTrace,
                urlHref: environmentService.get('location')?.href,
                errorMessage: error?.message,
                correlationId: error?.correlationId,
                origin: environmentService.get('location')?.origin,
            });
        }

        return next(action);
    };
